import Utils from '../../../util';
import InputSummaryUtils from '../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../data-structures/unit-value.model';
import { InputSummary, InputSummaryItem } from '../../input-summary';
import { AdditionalProduct } from './additional-product.model';
import { BiogasShare } from './biogas-share.model';
import { EnergyGenerationUnit, EnergyGenPropName } from './energy-generation-unit.model';
import { Fuel } from './fuel.model';
import { GasUpgradingUnit } from './gas-upgrading-unit.model';
import { Product } from './product.model';

export enum DigesterType {
  WET_MIXED = 'PROJECT.PLANT_SPEC.DIGESTER_TYPES.WET_MIXED',
  WET_LAGOON = 'PROJECT.PLANT_SPEC.DIGESTER_TYPES.WET_LAGOON',
  DRY_CONT = 'PROJECT.PLANT_SPEC.DIGESTER_TYPES.DRY_CONT',
  DRY_BATCH = 'PROJECT.PLANT_SPEC.DIGESTER_TYPES.DRY_BATCH',
  OTHER = 'PROJECT.PLANT_SPEC.DIGESTER_TYPES.OTHER',
}

export enum PlantUnitType {
  CHP = 'PROJECT.PLANT_SPEC.CHP',
  GENERATOR = 'PROJECT.PLANT_SPEC.GENERATOR',
  BOILER = 'PROJECT.PLANT_SPEC.BOILER',
  UPGRADER = 'PROJECT.PLANT_SPEC.UPGRADER',
  DEWATERING = 'PROJECT.PLANT_SPEC.DEWATERING'
}

export enum DigesterPeriodType {
  HRT = 'hrt',
  OLR = 'olr',
  OWV = 'owv'
}

export interface PlantSpecificationsInterface {
  selectedDigester: string;
  digesterName: string; // only for OTHER

  digesterEfficiency: UnitValue;
  digesterTemperature: UnitValue;
  digesterEfficiencyEnabled: boolean;
  digesterTemperatureEnabled:boolean;
  selectedUnits: string[];

  overheadVolume: UnitValue; // all except OTHER
  targetTS: UnitValue; // non dry-batch, not OTHER
  periodHRT: UnitValue; // all
  periodOLR: UnitValue; // non dry-batch 
  overallWorkingVolume: UnitValue;
  periodType: string; // all
  workingDays: UnitValue; // dry batch

  electricalEfficiency: UnitValue; // CHP & generator
  thermalEfficiency: UnitValue; // CHP & boiler
  methaneRecovery: UnitValue; // upgrader

  chp: EnergyGenerationUnit;
  generator: EnergyGenerationUnit;
  boiler: EnergyGenerationUnit;
  upgrader: GasUpgradingUnit;

  biogasShareData: BiogasShare[];

  dewateringType: string;
  cakeDryness: UnitValue;
  recovery: UnitValue;
  recycle: UnitValue;

  consumedFuels: Fuel[];
  generatedProducts: Product[];
  additionalProducts: AdditionalProduct[];
}

export class PlantSpecifications {
  public selectedDigester: string;
  public digesterName: string;
  public digesterEfficiency: UnitValue;
  public digesterTemperature: UnitValue;
  public digesterEfficiencyEnabled: boolean;
  public digesterTemperatureEnabled:boolean;
  public selectedUnits: string[];

  public overheadVolume: UnitValue;
  public targetTS: UnitValue;
  public periodHRT: UnitValue;
  public periodOLR: UnitValue;
  public overallWorkingVolume: UnitValue;
  public periodType: string;
  public workingDays: UnitValue;

  public electricalEfficiency: UnitValue;
  public thermalEfficiency: UnitValue;
  public methaneRecovery: UnitValue;

  public chp: EnergyGenerationUnit;
  public generator: EnergyGenerationUnit;
  public boiler: EnergyGenerationUnit;
  public upgrader: GasUpgradingUnit;

  public biogasShareData: BiogasShare[];

  public dewateringType: string;
  public cakeDryness: UnitValue;
  public recovery: UnitValue;
  public recycle: UnitValue;

  public consumedFuels: Fuel[];
  public generatedProducts: Product[];
  public additionalProducts: AdditionalProduct[];

  constructor(param: PlantSpecificationsInterface = {
    selectedDigester: '',
    digesterName: '',
    digesterEfficiency: new UnitValue({value: 100, unit: '000000000000000000000101'}),
    digesterTemperature: new UnitValue(),
    digesterEfficiencyEnabled: false,
    digesterTemperatureEnabled:false,
    selectedUnits: [],
    overheadVolume: new UnitValue({value: null, unit: '000000000000000000001801'}),
    targetTS: new UnitValue({value: null, unit: '000000000000000000000101'}),
    periodHRT: new UnitValue({value: null, unit: '000000000000000000001302'}),
    periodOLR: new UnitValue(),
    overallWorkingVolume: new UnitValue(),
    periodType: 'hrt',
    workingDays: new UnitValue({value: null, unit: '000000000000000000001302'}),
    electricalEfficiency: new UnitValue(),
    thermalEfficiency: new UnitValue(),
    methaneRecovery: new UnitValue(),
    chp: new EnergyGenerationUnit(),
    generator: new EnergyGenerationUnit(),
    boiler: new EnergyGenerationUnit(),
    upgrader: new GasUpgradingUnit(),
    biogasShareData: [],
    dewateringType: '',
    cakeDryness: new UnitValue(),
    recovery: new UnitValue(),
    recycle: new UnitValue(),
    consumedFuels: [],
    generatedProducts: [],
    additionalProducts: []
  }) {
    this.selectedDigester = param.selectedDigester;
    this.digesterName = param.digesterName ? param.digesterName : '';
    this.digesterName
    this.selectedUnits = param.selectedUnits;
    this.overheadVolume = new UnitValue(param.overheadVolume);
    this.targetTS = new UnitValue(param.targetTS);
    this.periodHRT = new UnitValue(param.periodHRT);
    this.periodOLR = new UnitValue(param.periodOLR);
    this.overallWorkingVolume = new UnitValue(param.overallWorkingVolume);
    if (['olr', 'hrt', 'owv'].includes(param.periodType)) {
      this.periodType = param.periodType;
    } else {
      this.periodType = DigesterPeriodType.HRT;
    }
    this.workingDays = new UnitValue(param.workingDays);
    this.electricalEfficiency = new UnitValue(param.electricalEfficiency);
    this.thermalEfficiency = new UnitValue(param.thermalEfficiency);
    this.methaneRecovery = new UnitValue(param.methaneRecovery);

    this.chp = new EnergyGenerationUnit(param.chp);
    this.generator = new EnergyGenerationUnit(param.generator);
    this.boiler = new EnergyGenerationUnit(param.boiler);
    this.upgrader = new GasUpgradingUnit(param.upgrader);

    this.biogasShareData = [];
    for (const data of param.biogasShareData) {
      this.biogasShareData.push(new BiogasShare(data));
    }
    this.dewateringType = param.dewateringType;
    this.cakeDryness = new UnitValue(param.cakeDryness);
    this.recovery = new UnitValue(param.recovery);
    this.recycle = new UnitValue(param.recycle);
    this.consumedFuels = [];
    for (const fuel of param.consumedFuels) {
      this.consumedFuels.push(new Fuel(fuel));
    }
    this.generatedProducts = [];
    for (const product of param.generatedProducts) {
      this.generatedProducts.push(new Product(product));
    }
    this.additionalProducts = [];
    if (param.additionalProducts) {
      for (const product of param.additionalProducts) {
        this.additionalProducts.push(new AdditionalProduct(product));
      }
    }

    if(param.digesterEfficiencyEnabled){
      this.digesterEfficiencyEnabled = param.digesterEfficiencyEnabled;
    } else {
      this.digesterEfficiencyEnabled = false;
    }

    if(param.digesterTemperatureEnabled){
      this.digesterTemperatureEnabled = param.digesterTemperatureEnabled;
    } else {
      this.digesterTemperatureEnabled = false;
    }
    
     if(param.digesterEfficiency){
      this.digesterEfficiency=new UnitValue(param.digesterEfficiency)
     }else{
      this.digesterEfficiency = new UnitValue({value: 100, unit: '000000000000000000000101'});
     }

     if(param.digesterTemperature){
      this.digesterTemperature=new UnitValue(param.digesterTemperature)
     }else{
      this.digesterTemperature = new UnitValue();
     }
    }

  static deepCopy(plantSpec: PlantSpecifications) {
    let copy;
    if (plantSpec) {
      copy = new PlantSpecifications(plantSpec);
      return copy;
    }
    throw new Error('PlantSpecifications copy failed');
  }

  getInputSummary(path: string[]) {
    const inputSummary = new InputSummary();

    // ----- digester -----
    const digesterInputSummary = new InputSummary();
    digesterInputSummary.subSection[''] = this.getDigesterInputs(path);
    inputSummary.subSection['PROJECT.PLANT_SPEC.DIGESTER'] = digesterInputSummary;

    // ----- plant units -----
    const plantUnitsInputSummary = new InputSummary();
    for (const unit of this.selectedUnits) {
      plantUnitsInputSummary.subSection[unit] = this.getPlantUnitInputs(unit, path);
    }
    if (this.hasDewatering()) {
      plantUnitsInputSummary.subSection['PROJECT.PLANT_SPEC.DEWATERING'] = this.getDewateringInputs(path);
    }
    inputSummary.subSection['INPUT_SUMMARY.PLANT_SPECIFICATIONS.SELECTED_UNITS'] = plantUnitsInputSummary;

    // ----- generated products -----
    const genProductInputSummary = new InputSummary();
    for (const genProd of this.generatedProducts) {
      const index = this.generatedProducts.indexOf(genProd);
      genProductInputSummary.subSection[genProd.name] = genProd.getInputSummary([...path, 'generatedProducts', String(index)]);
    }
    inputSummary.subSection[PlantSpecificationsPropName.generatedProducts] = genProductInputSummary;

    // ----- additional products -----
    const addProductInputSummary = new InputSummary();
    for (const addProd of this.additionalProducts) {
      const index = this.additionalProducts.indexOf(addProd);
      genProductInputSummary.subSection[addProd.name] = addProd.getInputSummary([...path, 'additionalProducts', String(index)]);
      for(const input of genProductInputSummary.subSection[addProd.name].inputs) {
        if(input.name==='parentProduct') {
          if(Utils.doesLookLikeUUID(input.value)) {
            input.value = this.getParentProductNameFromUUID(input.value);
          }
        }
      }
    }
    inputSummary.subSection[PlantSpecificationsPropName.additionalProducts] = addProductInputSummary;

    // ----- consumed fuels -----
    const conFuelInputSummary = new InputSummary();
    for (const conFuel of this.consumedFuels) {
      const index = this.consumedFuels.indexOf(conFuel);
      conFuelInputSummary.subSection[conFuel.name] = conFuel.getInputSummary([...path, 'consumedFuels', String(index)]);
    }
    inputSummary.subSection[PlantSpecificationsPropName.consumedFuels] = conFuelInputSummary;

    return inputSummary;
  }

  getParentProductNameFromUUID(uuid: string) {
    if(this.generatedProducts) {
      for(const generatedProduct of this.generatedProducts) {
        if(generatedProduct.uuid===uuid) {
          return generatedProduct.name;
        }
      }
    }
  }

  getDigesterInputs(path: string[]): InputSummary {
    const inputSummary = new InputSummary();

    let inputItems: InputSummaryItem[] = [];
    inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.selectedDigester, this.selectedDigester, [...path, 'selectedDigester']);
    inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.overheadVolume, this.overheadVolume, [...path, 'overheadVolume']);
    if(this.digesterTemperatureEnabled){
      inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.digesterTemperature, this.digesterTemperature, [...path,  'digesterTemperature']);
    }
    if (this.selectedDigester !== DigesterType.DRY_BATCH) {
      inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.targetTS, this.targetTS, [...path, 'targetTS']);
      if (this.periodType === DigesterPeriodType.HRT) {
        inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.periodHRT, this.periodHRT, [...path, 'periodHRT']);
      } else if (this.periodType === DigesterPeriodType.OWV) {
        inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.overallWorkingVolume, this.overallWorkingVolume, [...path, 'overallWorkingVolume']);
      } else {
        inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.periodOLR, this.periodOLR, [...path, 'periodOLR']);
      }
    } else {
      inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.residenceTime, this.periodHRT, [...path, 'periodHRT']);
      inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.workingDays, this.workingDays, [...path, 'workingDays']);
    }

    inputSummary.inputs = inputItems;
    return inputSummary;
  }

  getPlantUnitInputs(unit, path: string[]): InputSummary {
    const inputSummary = new InputSummary();

    let inputItems: InputSummaryItem[] = [];
    if (unit === PlantUnitType.CHP) {
      inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.electricalEfficiency, this.electricalEfficiency, [...path, 'electricalEfficiency']);
      inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.thermalEfficiency, this.thermalEfficiency, [...path, 'thermalEfficiency']);
      if (this.chp.nominalCapacityToggle) {
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.nominalCapacity, this.chp.nominalCapacity, [...path, 'nominalCapacity']);
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.minimumOutput, this.chp.minimumOutput, [...path, 'minimumOutput']);
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.heatingValue, this.chp.heatingValue, [...path, 'heatingvalue']);
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.workingPeriod, this.chp.workingPeriod, [...path, 'workingPeriod']);
      }
    } else if (unit === PlantUnitType.GENERATOR) {
      inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.electricalEfficiency, this.electricalEfficiency, [...path, 'electricalEfficiency']);
      if (this.generator.nominalCapacityToggle) {
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.nominalCapacity, this.generator.nominalCapacity, [...path, 'nominalCapacity']);
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.minimumOutput, this.generator.minimumOutput, [...path, 'minimumOutput']);
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.heatingValue, this.generator.heatingValue, [...path, 'heatingvalue']);
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.workingPeriod, this.generator.workingPeriod, [...path, 'workingPeriod']);
      }
    } else if (unit === PlantUnitType.BOILER) {
      inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.thermalEfficiency, this.thermalEfficiency, [...path, 'thermalEfficiency']);
      if (this.boiler.nominalCapacityToggle) {
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.nominalCapacity, this.boiler.nominalCapacity, [...path, 'nominalCapacity']);
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.minimumOutput, this.boiler.minimumOutput, [...path, 'minimumOutput']);
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.heatingValue, this.boiler.heatingValue, [...path, 'heatingvalue']);
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.workingPeriod, this.boiler.workingPeriod, [...path, 'workingPeriod']);
      }
    } else if (unit === PlantUnitType.UPGRADER) {
      inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.methaneRecovery, this.methaneRecovery, [...path, 'methaneRecovery']);
      if (this.upgrader.nominalCapacityToggle) {
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.nominalCapacity, this.upgrader.nominalCapacity, [...path, 'nominalCapacity']);
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.minimumOutput, this.upgrader.minimumOutput, [...path, 'minimumOutput']);
        inputItems = InputSummaryUtils.performPush(inputItems, EnergyGenPropName.workingPeriod, this.upgrader.workingPeriod, [...path, 'workingPeriod']);
      }
    }
    for (const share of this.biogasShareData) {
      if (share.name === unit) {
        const index = this.biogasShareData.indexOf(share);
        inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.biogasShareData, share.share, [...path, 'biogasShareData', String(index), 'share']);
      }
    }

    inputSummary.inputs = inputItems;
    return inputSummary;
  }

  getDewateringInputs(path: string[]): InputSummary {
    const inputSummary = new InputSummary();

    let inputItems: InputSummaryItem[] = [];
    if (this.dewateringType !== null) {
      inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.dewateringType, this.dewateringType, [...path, 'dewateringType']);
    }
    inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.cakeDryness, this.cakeDryness, [...path, 'cakeDryness']);
    inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.recovery, this.recovery, [...path, 'recovery']);
    inputItems = InputSummaryUtils.performPush(inputItems, PlantSpecificationsPropName.recycle, this.recycle, [...path, 'recycle']);

    inputSummary.inputs = inputItems;
    return inputSummary;
  }

  /**
   * Determines if the current setup of the plant specification has a dewatering unit.
   */
  hasDewatering(): boolean {
    if (this.selectedUnits) {
      return this.selectedUnits.includes(PlantUnitType.DEWATERING);
    }
    throw Error('Selected units has not been initialized, unable to process hasDewatering.');
  }

  digesterPeriodUnitSelected(): boolean {
    if (this.periodType) {
      if (this.periodType === DigesterPeriodType.HRT) {
        if (this.periodHRT && this.periodHRT.unit) {
          return true;
        }
      } else if (this.periodType === DigesterPeriodType.OLR) {
        if (this.periodOLR && this.periodOLR.unit) {
          return true;
        }
      }
    }
    return false;
  }

  digesterPeriodHasOlrCodUnit(): boolean {
    if (this.periodType === DigesterPeriodType.OLR) {
      return this.periodOLR.unit === '000000000000000000003602' || this.periodOLR.unit === '000000000000000000003605';
    }
  }

  /**
   * Return a boolean for if plant spec if setup for dry batch.
   */
  isDryBatch() {
    return this.selectedDigester === DigesterType.DRY_BATCH;
  }


  /**
   * Returns the biogas share for the specified processing node
   */
  getBiogasShareFromType(type: string): number {
    for (const biogasShareItem of this.biogasShareData) {
      if (type === biogasShareItem.name) {
        return biogasShareItem.share.value;
      }
    }
    return 0;
  }
}

/**
 * An enum that contains UI friendly names for each member of the Plant Spec class
 */
export enum PlantSpecificationsPropName {
  selectedDigester = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.SELECTED_DIGESTER',
  digesterName = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.DIGESTER_NAME',
  selectedUnits = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.SELECTED_UNITS',

  overheadVolume = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.OVERHEAD_VOLUME',
  targetTS = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.TARGETED_TS',
  residenceTime = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.RESIDENCE_TIME',
  periodHRT = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PERIOD_HRT',
  periodOLR = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.PERIOD_OLR',
  overallWorkingVolume = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.OVERALL_WORKING_VOLUME',
  workingDays = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.WORKING_DAYS',
  digesterEfficiency = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.DIGESTER_EFFICIENCY',
  digesterTemperature = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.DIGESTER_TEMPERATURE',
  electricalEfficiency = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.ELECTRICAL_EFFICIENCY',
  thermalEfficiency = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.THERMAL_EFFICIENCY',

  methaneRecovery = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.METHANE_RECOVERY',

  biogasShareData = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.BIOGAS_SHARE_DATA',
  dewateringType = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.DEWATERING_TYPE',
  cakeDryness = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.CAKE_DRYNESS',
  recovery = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.RECOVERY',

  recycle = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.RECYCLE',
  generatedProducts = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.GENERATED_PRODUCTS',
  additionalProducts = 'additional products',
  consumedFuels = 'INPUT_SUMMARY.PLANT_SPECIFICATIONS.CONSUMED_FUELS',
  priceStructures = 'price structures',

  id_selectedDigester = 'digester type',
  id_digesterName = 'digester name',
  id_selectedUnits = 'plant units',

  id_overheadVolume = 'overhead volume',
  id_targetTS = 'targeted total solid',
  id_residenceTime = 'residence time',
  id_periodHRT = 'hydraulic retention time',
  id_periodOLR = 'organic loading rate',
  id_overallWorkingVolume = 'overallWorkingVolume',
  id_workingDays = 'working days',

  id_electricalEfficiency = 'electrical efficiency',
  id_thermalEfficiency = 'thermal efficiency',

  id_methaneRecovery = 'methane recovery',

  id_biogasShareData = 'biogas share of units',
  id_dewateringType = 'type',
  id_cakeDryness = 'solid cake dryness',
  id_recovery = 'solid recovery',
  id_digesterEfficiency = "Digester Efficiency",
  id_digesterTemperature = "digester, digester temperature",
  id_recycle = 'portion of recycling',
  id_generatedProducts = 'generated product',
  id_consumedFuels = 'consumed fuel',

}
